import React, { useEffect } from 'react';

import * as styles from './styles';

import type { Breadcrumb, Vehicle } from '@cobliteam/common-types';

import {
  isWebDataError,
  isWebDataFetching,
  isWebDataSuccess,
  type MaintenanceRemindersSetup,
  type WebData,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  BodyText,
  Breadcrumbs,
  ButtonGroup,
  CloseDrawerButton,
  Dialog,
  Divider,
  Heading1,
  Loading,
  ModalType,
  NoConnectionIcon,
  PermissionsGate,
  Placeholder,
  PlaceholderVehicleIcon,
  ReminderPanel,
} from '@cobliteam/base-components';

import { WarningRounded } from '@material-ui/icons';

import {
  PastMaintenanceSetup,
  type VehiclesLastMaintenanceDate,
  type VehiclesLastOdometer,
} from './components/PastMaintenanceSetup/PastMaintenanceSetup';
import {
  type VehicleOdometer,
  VehicleSelection,
} from './components/VehicleSelection/VehicleSelection';

import MaintenancePermissions from '../../permissions';
import { LIMIT_NUMBER_RECURRENCES } from '../constants';
import {
  RecurrenceInputs,
  type RecurrenceItems,
} from '../RecurrenceInputs/RecurrenceInputs';

enum SetupRemindersStep {
  INFO = 'Informações dos veículos',
  PAST = 'Manutenções passadas',
  RECURRENCE = 'Recorrência dos lembretes',
}

export interface SetupRemindersModalProps {
  vehiclesWithoutRemindersWebData: WebData<Vehicle[]>;
  loadVehiclesWithOdometer: () => void;
  onClose: () => void;
  onConfirm: (data: MaintenanceRemindersSetup) => void;
}

const steps: Breadcrumb[] = [
  {
    id: SetupRemindersStep.INFO,
    title: `1. ${SetupRemindersStep.INFO}`,
  },
  {
    id: SetupRemindersStep.PAST,
    title: `2. ${SetupRemindersStep.PAST}`,
  },
  {
    id: SetupRemindersStep.RECURRENCE,
    title: `3. ${SetupRemindersStep.RECURRENCE}`,
  },
];

export const SetupRemindersModal: React.FC<SetupRemindersModalProps> = ({
  vehiclesWithoutRemindersWebData,
  loadVehiclesWithOdometer,
  onClose,
  onConfirm,
}) => {
  const [currentStep, setCurrentStep] = React.useState(steps[0]);

  const [vehiclesCurrentOdometer, setVehiclesCurrentOdometer] =
    React.useState<VehicleOdometer>({});
  const [vehiclesLastMaintenanceDate, setVehiclesLastMaintenanceDate] =
    React.useState<VehiclesLastMaintenanceDate>({});
  const [vehiclesLastOdometer, setVehiclesLastOdometer] =
    React.useState<VehiclesLastOdometer>({});
  const [recurrenceItems, setRecurrenceItems] = React.useState<RecurrenceItems>(
    {}
  );

  useEffect(() => {
    loadVehiclesWithOdometer();
  }, []);

  const handleStepBack = () => {
    const currentStepIndex = steps.findIndex((s) => s.id === currentStep.id);
    setCurrentStep(steps[currentStepIndex - 1]);
  };

  const getVehiclesToSetup = (vehicles: Vehicle[]) => {
    const vehicleIdsToSetup = Object.keys(vehiclesCurrentOdometer);
    return vehicles.filter((vehicle) => vehicleIdsToSetup.includes(vehicle.id));
  };

  const getVehiclesForFirstPage = (vehicles: Vehicle[]): Vehicle[] => {
    const vehiclesWithOdometer = vehicles.filter(
      (vehicle) => vehicle.odometer?.odometerInKm !== null
    );
    const vehiclesWithoutOdometer = vehicles.filter(
      (vehicle) => vehicle.odometer?.odometerInKm === null
    );
    const vehiclesWithoutOdometerWithDevice = vehiclesWithoutOdometer.filter(
      (vehicle) => vehicle.deviceId !== null
    );
    const vehiclesWithoutOdometerWithoutDevice = vehiclesWithoutOdometer.filter(
      (vehicle) => vehicle.deviceId === null
    );

    const sortedVehiclesWithOdometer = vehiclesWithOdometer.sort(
      (firstVehicle, secondVehicle) =>
        (firstVehicle.licensePlate || '').localeCompare(
          secondVehicle.licensePlate || ''
        )
    );
    const sortedvehiclesWithoutOdometerWithDevice =
      vehiclesWithoutOdometerWithDevice.sort((firstVehicle, secondVehicle) =>
        (firstVehicle.licensePlate || '').localeCompare(
          secondVehicle.licensePlate || ''
        )
      );
    const sortedvehiclesWithoutOdometerWithoutDevice =
      vehiclesWithoutOdometerWithoutDevice.sort((firstVehicle, secondVehicle) =>
        (firstVehicle.licensePlate || '').localeCompare(
          secondVehicle.licensePlate || ''
        )
      );

    return [
      ...sortedVehiclesWithOdometer,
      ...sortedvehiclesWithoutOdometerWithDevice,
      ...sortedvehiclesWithoutOdometerWithoutDevice,
    ];
  };

  const canMoveFromFirstPage = () => {
    const entries = Object.entries(vehiclesCurrentOdometer);
    return entries.length > 0;
  };

  const canMoveFromSecondPage = (vehicles: Vehicle[]) => {
    const selectedVehicles = getVehiclesToSetup(vehicles);
    return selectedVehicles.every((vehicle) => {
      const currentOdometer = vehiclesCurrentOdometer[vehicle.id];
      const pastOdometer = vehiclesLastOdometer[vehicle.id];
      const pastDate = vehiclesLastMaintenanceDate[vehicle.id];

      if (pastDate || pastOdometer) {
        if (pastOdometer !== undefined && pastDate !== undefined) {
          return currentOdometer && pastOdometer < currentOdometer;
        }
        // Both values must be filled if one of them was set
        return false;
      }
      // None of them were, which is ok because they are optional
      return true;
    });
  };

  const canMoveFromThirdPage = () => {
    const items = Object.values(recurrenceItems);
    return (
      items.length > 0 && !items.some((item) => !item.valueInKm || item.error)
    );
  };

  const handleSetupConfirm = (vehicles: Vehicle[]) => {
    const selectedVehicles = getVehiclesToSetup(vehicles);
    Analytics.track('Maintenance Reminders Setup Submited', {
      numVehicles: selectedVehicles.length,
    });

    const recurrenceInMeters = Object.values(recurrenceItems).map(
      (recurrence) => (recurrence.valueInKm || 0) * 1000
    );
    const vehiclesMaintenanceSetup = selectedVehicles.map((vehicle) => ({
      vehicleId: vehicle.id,
      currentOdometerInMeters:
        (vehiclesCurrentOdometer[vehicle.id] || 0) * 1000,
      lastMaintenanceDate: vehiclesLastMaintenanceDate[vehicle.id],
      odometerInMetersAtThatDate:
        (vehiclesLastOdometer[vehicle.id] || 0) * 1000 || undefined,
    }));

    const recurrenceNames = Object.values(recurrenceItems).reduce(
      (acc, recurrence) => {
        const recurrenceInMetersIndex = (recurrence.valueInKm || 0) * 1000;
        acc[recurrenceInMetersIndex] = recurrence.name || '';
        return acc;
      },
      {} as { [value: number]: string }
    );

    onConfirm({
      vehiclesMaintenanceSetup,
      recurrenceInMeters,
      recurrenceNames,
    });
  };

  const renderFirstPage = (vehicles: Vehicle[]) => (
    <>
      <div id='firstPage' className={styles.page}>
        <BodyText>
          Selecione os veículos que deseja programar lembretes de manutenção.
        </BodyText>
        <ReminderPanel
          className={styles.reminder}
          textClassName={styles.reminderText}
          iconClassName={styles.reminderIcon}
          Icon={WarningRounded}
        >
          <PermissionsGate
            anyOfPermissions={
              MaintenancePermissions.CREATE_UPDATE_DELETE_VEHICLE
            }
            fallback={
              'Para editar as informações de odômetro, acesse a aba Minha frota. ' +
              '(Caso não possua accesso, solicite a outro usuário do painel.)'
            }
          >
            {'Para editar o valor do odômetro clique no botão de Editar veículo ou vá até ' +
              'Minha frota > Veículos. Os valores serão atualizados automaticamente aqui.'}
          </PermissionsGate>
        </ReminderPanel>
      </div>
      <VehicleSelection
        vehiclesWithoutReminder={vehicles}
        vehiclesCurrentOdometer={vehiclesCurrentOdometer}
        onChange={(data) => setVehiclesCurrentOdometer(data)}
      />
      <div className={styles.page}>
        <ButtonGroup
          onCancel={onClose}
          onConfirm={() => setCurrentStep(steps[1])}
          buttonText='Próximo'
          confirmationButtonProps={{
            disabled: !canMoveFromFirstPage(),
          }}
          type={ModalType.SUBMIT}
        />
      </div>
    </>
  );

  const renderSecondPage = (vehicles: Vehicle[]) => (
    <div className={styles.page}>
      <BodyText>
        Insira as informações de manutenções passadas de cada veículo para ter
        lembretes mais precisos. Se você escolher não preencher essas
        informações, vamos calcular os lembretes a partir da data e odômetro de
        hoje.
      </BodyText>
      <PastMaintenanceSetup
        vehiclesLastMaintenanceDate={vehiclesLastMaintenanceDate}
        vehiclesLastOdometer={vehiclesLastOdometer}
        vehiclesOdometer={vehiclesCurrentOdometer}
        onChangeVehiclesLastMaintenanceDate={(data) =>
          setVehiclesLastMaintenanceDate(data)
        }
        onChangeVehiclesLastOdometer={(data) => setVehiclesLastOdometer(data)}
        vehiclesToSetup={getVehiclesToSetup(vehicles)}
      />
      <ButtonGroup
        onCancel={onClose}
        onConfirm={() => setCurrentStep(steps[2])}
        buttonText='Próximo'
        confirmationButtonProps={{
          disabled: !canMoveFromSecondPage(vehicles),
        }}
        type={ModalType.SUBMIT}
      />
    </div>
  );

  const renderThirdPage = (vehicles: Vehicle[]) => (
    <div className={styles.page}>
      <BodyText>
        Escolha quando você precisa ser lembrado sobre próximas manutenções, a
        partir dos quilômetros rodados pelos veículos, e indique o que precisa
        ser feito. Adicione até {LIMIT_NUMBER_RECURRENCES} lembretes.
      </BodyText>
      <RecurrenceInputs
        className={styles.recurrenceInput}
        items={recurrenceItems}
        onItemsChange={(data) => setRecurrenceItems(data)}
      />
      <ButtonGroup
        onCancel={onClose}
        onConfirm={() => handleSetupConfirm(vehicles)}
        buttonText='Salvar'
        confirmationButtonProps={{ disabled: !canMoveFromThirdPage() }}
        type={ModalType.SUBMIT}
      />
    </div>
  );

  return (
    <Dialog open maxWidth='md' scroll='body'>
      <div className={styles.modal}>
        <div className={styles.header}>
          <Breadcrumbs
            breadcrumbs={steps}
            selectedBreadcrumb={currentStep}
            onBreadcrumbClick={setCurrentStep}
            onReturnButtonClick={
              currentStep.id !== SetupRemindersStep.INFO
                ? handleStepBack
                : undefined
            }
          />
          <CloseDrawerButton
            onCloseConfirmed={onClose}
            shouldConfirm={canMoveFromFirstPage()}
          />
        </div>

        <div className={styles.title}>
          <Heading1>{currentStep.id}</Heading1>
          <Divider />
        </div>

        {isWebDataSuccess(vehiclesWithoutRemindersWebData) &&
          vehiclesWithoutRemindersWebData.data.length > 0 && (
            <>
              {currentStep.id === SetupRemindersStep.INFO &&
                renderFirstPage(
                  getVehiclesForFirstPage(vehiclesWithoutRemindersWebData.data)
                )}
              {currentStep.id === SetupRemindersStep.PAST &&
                renderSecondPage(vehiclesWithoutRemindersWebData.data)}
              {currentStep.id === SetupRemindersStep.RECURRENCE &&
                renderThirdPage(vehiclesWithoutRemindersWebData.data)}
            </>
          )}
        {isWebDataSuccess(vehiclesWithoutRemindersWebData) &&
          vehiclesWithoutRemindersWebData.data.length === 0 && (
            <Placeholder
              className={styles.placeholderWithoutTextButton}
              text='Não há veículos sem lembretes de manutenção configurados.'
              Icon={PlaceholderVehicleIcon}
            />
          )}
        {isWebDataError(vehiclesWithoutRemindersWebData) && (
          <Placeholder
            className={styles.placeholderWithoutTextButton}
            text='Ops! Ocorreu um erro no sistema.'
            Icon={NoConnectionIcon}
          />
        )}
        {isWebDataFetching(vehiclesWithoutRemindersWebData) && (
          <Loading className={styles.loading} />
        )}
      </div>
    </Dialog>
  );
};
